import { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';

/** Cache expires after 5 minutes so mid-session membership changes
 *  (join / leave) are picked up without a full page refresh. */
export const __CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Module-level cache so multiple consumers on a single page render
 * (ShareToCommunityButton instances, route guards, etc.) share one
 * /api/memberships/me request. Keyed on user id — when a different user
 * signs in the key changes and the cache misses, forcing a fresh fetch.
 *
 * The cache uses a 5-minute TTL so that mid-session membership changes
 * are picked up without requiring a page refresh.
 */
let cachedUserId = null;
let cachedPromise = null;
let cachedAt = 0;

/**
 * Test-only: clear the module-level membership cache so each test starts from a
 * clean slate. Application code never calls this -- the cache intentionally
 * lives for the page's lifetime and re-syncs on refresh.
 */
export function __resetMembershipCache() {
    cachedUserId = null;
    cachedPromise = null;
    cachedAt = 0;
}

function fetchActiveMemberships(api, userId) {
    if (
        cachedUserId === userId &&
        cachedPromise &&
        Date.now() - cachedAt < __CACHE_TTL_MS
    ) {
        return cachedPromise;
    }
    cachedUserId = userId;
    cachedAt = Date.now();
    cachedPromise = api
        .get('/api/memberships/me')
        .then((response) => {
            const data = Array.isArray(response.data) ? response.data : [];
            return data.filter((m) => m.status === 'active');
        })
        .catch((err) => {
            cachedUserId = null;
            cachedPromise = null;
            cachedAt = 0;
            throw err;
        });
    return cachedPromise;
}

/**
 * Fetches the authenticated user's own community memberships and filters
 * for active memberships (any role). Returns the list along with
 * loading/error state.
 *
 * @returns {{
 *   communities: Array<{_id, communityId, community: {_id, name, slug}, role, status}>,
 *   hasCommunities: boolean,
 *   loading: boolean,
 *   error: Error|null
 * }}
 */
export default function useUserCommunities() {
    const { user, profileLoading } = useAuth();
    const api = useAuthenticatedAxios();

    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (profileLoading) return;
        if (!user) {
            setLoading(false);
            setCommunities([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchActiveMemberships(api, user.uid)
            .then((active) => {
                if (cancelled) return;
                setCommunities(active);
                setError(null);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [user, profileLoading, api]);

    return {
        communities,
        hasCommunities: communities.length > 0,
        loading,
        error,
    };
}
